import type { WidgetPosition } from './position'
import type { WidgetPreferences } from './types'

export interface WidgetConfigPersist {
  position?: WidgetPosition
  language?: string
  theme?: { primaryColor?: string; mode?: 'light' | 'dark' | 'auto' }
  widget?: 'ticket-form' | 'support-portal'
}

const STORAGE_PREFIX = 'reqdesk_'
const PREFS_KEY_PREFIX = `${STORAGE_PREFIX}prefs_`

const KNOWN_PREF_KEYS: readonly (keyof WidgetPreferences)[] = [
  'language',
  'themeMode',
  'primaryColor',
  'position',
  'display',
]

const unknownKeyWarnings = new Set<string>()

function filterKnownKeys(input: unknown, sourceLabel: string): WidgetPreferences {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {}
  const out: WidgetPreferences = {}
  const knownSet = new Set<string>(KNOWN_PREF_KEYS as readonly string[])
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (knownSet.has(key)) {
      (out as Record<string, unknown>)[key] = value
    } else if (!unknownKeyWarnings.has(key)) {
      unknownKeyWarnings.add(key)
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn(`[reqdesk] preferences: unknown key "${key}" (from ${sourceLabel}); ignoring`)
      }
    }
  }
  return out
}

function getStorage(): Storage | null {
  try {
    const s = window.localStorage
    s.setItem('__test__', '1')
    s.removeItem('__test__')
    return s
  } catch {
    try {
      return window.sessionStorage
    } catch {
      return null
    }
  }
}

export function saveTrackingToken(projectSlug: string, token: string): void {
  const storage = getStorage()
  if (!storage) return

  const key = `${STORAGE_PREFIX}tokens_${projectSlug}`
  try {
    const existing = JSON.parse(storage.getItem(key) ?? '[]') as string[]
    if (!existing.includes(token)) {
      existing.push(token)
      storage.setItem(key, JSON.stringify(existing))
    }
  } catch {
    storage.setItem(key, JSON.stringify([token]))
  }
}

export function getTrackingTokens(projectSlug: string): string[] {
  const storage = getStorage()
  if (!storage) return []

  const key = `${STORAGE_PREFIX}tokens_${projectSlug}`
  try {
    return JSON.parse(storage.getItem(key) ?? '[]') as string[]
  } catch {
    return []
  }
}

export function saveWidgetConfig(apiKey: string, config: WidgetConfigPersist): void {
  const storage = getStorage()
  if (!storage) return

  const key = `${STORAGE_PREFIX}config_${apiKey}`
  try {
    const existing = loadWidgetConfig(apiKey)
    const merged = { ...existing, ...config }
    if (config.theme && existing?.theme) {
      merged.theme = { ...existing.theme, ...config.theme }
    }
    storage.setItem(key, JSON.stringify(merged))
  } catch {
    // Silently fail if storage is full or unavailable
  }
}

export function saveWidgetEmail(apiKey: string, email: string): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.setItem(`${STORAGE_PREFIX}email_${apiKey}`, email)
  } catch { /* ignore */ }
}

export function loadWidgetEmail(apiKey: string): string | null {
  const storage = getStorage()
  if (!storage) return null
  try {
    return storage.getItem(`${STORAGE_PREFIX}email_${apiKey}`)
  } catch {
    return null
  }
}

export function clearWidgetEmail(apiKey: string): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(`${STORAGE_PREFIX}email_${apiKey}`)
  } catch { /* ignore */ }
}

export function saveSelectedProject(apiKey: string, userIdentifier: string, projectId: string): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.setItem(`${STORAGE_PREFIX}project_${apiKey}_${userIdentifier}`, projectId)
  } catch { /* ignore */ }
}

export function loadSelectedProject(apiKey: string, userIdentifier: string): string | null {
  const storage = getStorage()
  if (!storage) return null
  try {
    return storage.getItem(`${STORAGE_PREFIX}project_${apiKey}_${userIdentifier}`)
  } catch {
    return null
  }
}

export function loadWidgetConfig(apiKey: string): WidgetConfigPersist | null {
  const storage = getStorage()
  if (!storage) return null

  const key = `${STORAGE_PREFIX}config_${apiKey}`
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as WidgetConfigPersist
  } catch {
    return null
  }
}

// ─── Widget preferences (FR-031 precedence ladder) ───────────────────────────
//
// Precedence highest → lowest:
//   1. runtime setPreferences()  (applied by registry, not here)
//   2. userPreferences           (host-owned, passed at init)
//   3. localStorage              (device cache)
//   4. initialPreferences        (host-provided defaults at init)
//   5. built-in defaults         (widget's own fallback; caller applies)
//
// This resolver merges 2–4 into a single effective blob. Runtime mutations are
// layered on top by the registry; built-in defaults apply at render time when a
// key is still missing.

export interface ResolvePreferencesInput {
  apiKey: string
  initialPreferences?: WidgetPreferences
  userPreferences?: WidgetPreferences
}

export function loadPreferencesFromStorage(apiKey: string): WidgetPreferences {
  const storage = getStorage()
  if (!storage) return {}
  try {
    const raw = storage.getItem(PREFS_KEY_PREFIX + apiKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return filterKnownKeys(parsed, 'localStorage')
  } catch {
    return {}
  }
}

export function savePreferencesToStorage(apiKey: string, prefs: WidgetPreferences): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.setItem(PREFS_KEY_PREFIX + apiKey, JSON.stringify(prefs))
  } catch { /* ignore quota / availability errors */ }
}

export function resolvePreferences(input: ResolvePreferencesInput): WidgetPreferences {
  const initial = filterKnownKeys(input.initialPreferences ?? {}, 'initialPreferences')
  const stored = loadPreferencesFromStorage(input.apiKey)
  const user = filterKnownKeys(input.userPreferences ?? {}, 'userPreferences')
  return { ...initial, ...stored, ...user }
}

export function writePreferences(
  apiKey: string,
  next: WidgetPreferences,
  hostCallback?: (p: WidgetPreferences) => void,
): void {
  savePreferencesToStorage(apiKey, next)
  if (hostCallback) {
    try { hostCallback(next) } catch { /* ignore host callback errors */ }
  }
}
